import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '@/features/baseQuery';
import {
	collection,
	deleteDoc,
	doc,
	getDoc,
	getDocs,
	query,
	setDoc,
	Timestamp,
	updateDoc,
	where,
} from 'firebase/firestore';
import { firestore } from '@/utils/firebase';
import { userApi, getActingUser } from '../user/userApi';
import testClientJson from '@/assets/json/test-client.json';
import { safelyParseDate } from '@/utils/date';
import { isFirebaseId } from '@/utils/constants';
import { queryViewableRecords } from '@/utils/firebaseService';
import { chunk } from 'lodash';
import { buildTypesenseInFilter, TYPESENSE_CHUNK_SIZE } from '@/utils/typesense/util';
import { triggerRefresh } from '@/features/table/clientsTableSlice';
import { createClientAssignedNotification } from './helper';
import {
	INFORMED_CONSENTS_COLLECTION,
	getConsentRecordTime,
	mapInformedConsentDoc,
} from '@/features/informedConsent/helpers';
import {
	WHOLESALE_CLIENT,
	VULNERABLE_CLIENT,
} from '@/components/forms/CreateNewBusinessForm/validationSchema';

const CLIENT_COLLECTION = 'clients';

// Mirrors the cheap subset of parseInsightClientToFirebase (backend/functions/exports/clientApi/update.js)
// used to optimistically patch the client cache while an updateClientx request is in flight — the
// server response remains authoritative via the CLIENT/CLIENT_ROW/CLIENT_INSIGHT tag invalidations.
const deriveClientRowFieldsFromInsight = (insight) => {
	const { Name, CurrentStatusId, ClientTypeId, ComplianceTypeId, VulnerableClient } =
		insight ?? {};

	return {
		...(Name && { name: Name }),
		...(CurrentStatusId != null && { isCurrent: CurrentStatusId === 0 }),
		...(ClientTypeId != null && { isProspect: ClientTypeId === 1 }),
		...((ComplianceTypeId != null || VulnerableClient != null) && {
			clientCategory: [
				...(ComplianceTypeId === 2 ? [WHOLESALE_CLIENT] : []),
				...(VulnerableClient === true || VulnerableClient === 'Yes'
					? [VULNERABLE_CLIENT]
					: []),
			],
		}),
	};
};

const invalidateReferralPartnerClientCounts = (dispatch) => {
	dispatch(clientsApi.util.invalidateTags([{ type: 'CLIENTS_BY_REFERRAL_PARTNER', id: 'LIST' }]));
};

const getClientById = async (clientId, user) => {
	if (clientId == null) {
		return;
	}

	// Client doc ids are read directly; a numeric id (from an old link) is a HubSpot id,
	// matched on hubspotId among the clients the user can see.
	if (isFirebaseId(clientId)) {
		const clientDoc = await getDoc(doc(collection(firestore, CLIENT_COLLECTION), clientId));
		return clientDoc.exists() ? clientDoc : undefined;
	}

	const docs = await queryViewableRecords({
		collectionRef: collection(firestore, CLIENT_COLLECTION),
		constraints: [where('hubspotId', '==', clientId)],
		user,
	});
	return docs[0];
};

export const getClientFromCache = (state, clientId) => {
	return clientsApi.endpoints.getClientRow.select(clientId)(state)?.data;
};

const RTK_CHUNK_SIZE = 20;

const getClientsFromCache = (state, clientIds) => {
	const dataByClientId = new Map();

	for (const clientId of clientIds) {
		const id = clientId?.toString();
		const cachedData = getClientFromCache(state, id);
		if (cachedData) dataByClientId.set(id, cachedData);
	}

	return dataByClientId;
};

const getClientsFromTypesense = async (clientIds, dispatch) => {
	const dataByClientId = new Map();

	for (const idChunk of chunk(clientIds, TYPESENSE_CHUNK_SIZE)) {
		// Callers pass client doc ids, which are the Typesense `id`.
		const rows = await dispatch(
			clientsApi.endpoints.listClients.initiate({
				filters: buildTypesenseInFilter('id', idChunk),
			}),
		).unwrap();

		for (const row of rows) {
			const id = row.id?.toString();
			if (id) {
				dataByClientId.set(id, row);
			}
		}
	}

	return dataByClientId;
};

const getClientsFromRtk = async (clientIds, dispatch) => {
	const dataByClientId = new Map();

	for (const idChunk of chunk(clientIds, RTK_CHUNK_SIZE)) {
		const results = await Promise.all(
			idChunk.map(async (clientId) => {
				try {
					const data = await dispatch(
						clientsApi.endpoints.getClientRow.initiate(clientId, {
							subscribe: false,
						}),
					).unwrap();

					return [clientId, data ?? null];
				} catch {
					return [clientId, null];
				}
			}),
		);

		for (const [clientId, data] of results) {
			if (data) dataByClientId.set(clientId, data);
		}
	}

	return dataByClientId;
};

// Creating an API service using RTK Query
export const clientsApi = createApi({
	reducerPath: 'clientsApi',
	tagTypes: [
		'CLIENTS',
		'CLIENT',
		'CLIENT_ROW',
		'CLIENT_POLICIES',
		'CLIENTS_BY_REFERRAL_PARTNER',
		'INFORMED_CONSENT',
	],
	baseQuery: authBaseQuery,
	endpoints: (builder) => ({
		// Defining an endpoint to get a client's Insight data
		getClientInsight: builder.query({
			providesTags: (result, error, clientId) => [{ type: 'CLIENT_INSIGHT', id: clientId }],
			query: (clientId) => ({
				url: `${process.env.REACT_APP_CLIENT_API}/get`,
				method: 'POST',
				body: { clientId },
			}),
		}),
		getClientRow: builder.query({
			// Tagged by doc id, so the copy cached under the HubSpot id is invalidated too.
			providesTags: (result, error, clientId) => {
				return [{ type: 'CLIENT_ROW', id: result?.id ?? clientId }];
			},
			// Also cache the client under its other id (doc id or HubSpot id), so the
			// old-link redirect doesn't fetch it again.
			onQueryStarted: async (clientId, { dispatch, getState, queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled;
					const otherId = isFirebaseId(clientId) ? data?.hubspotId : data?.id;
					if (!otherId) return;
					const cached =
						clientsApi.endpoints.getClientRow.select(otherId)(getState())?.data;
					if (!cached) {
						dispatch(clientsApi.util.upsertQueryData('getClientRow', otherId, data));
					}
				} catch (error) {
					console.log('Error caching client row under its other id', error);
				}
			},
			queryFn: async (clientId, { getState, dispatch }) => {
				const userData = await getActingUser(dispatch, getState);

				const brokerGroupArray = userData?.brokerGroupArray ?? [];

				if (clientId === testClientJson?.clientRow?.clientId) {
					const testClientId = testClientJson?.clientRow?.clientId;
					const testClientData = {
						...testClientJson?.clientRow,
						id: testClientId,
						clientId: testClientId,
						docId: testClientId,
						brokerGroupId: userData?.brokerGroupId,
						ownerDocId: userData?.userId,
						canViewClient: true,
					};
					console.log('client data', { data: testClientData });
					return { data: testClientData };
				}

				const doc = await getClientById(clientId, userData);

				let brokerGroup;
				const brokerGroupId = doc?.data()?.brokerGroupId;
				const canViewClient = brokerGroupArray.includes(brokerGroupId);
				if (brokerGroupId) {
					try {
						const brokerGroupDoc = await dispatch(
							userApi.endpoints.getBrokerGroupDetails.initiate({
								docId: brokerGroupId,
							}),
						).unwrap();

						if (brokerGroupDoc?.id) {
							brokerGroup = brokerGroupDoc;
						} else {
							console.log('No such broker group document!');
						}
					} catch (error) {
						console.error('Error getting broker group:', error);
					}
				}

				const docData = JSON.parse(JSON.stringify(doc?.data?.() ?? {}));
				const data = doc
					? {
							...docData,
							id: doc.id,
							clientId: doc.id,
							docId: doc.id,
							nextTaskDate: safelyParseDate(doc.data()?.nextTaskDate)?.toISOString(),
							createdDate: safelyParseDate(doc.data()?.createdDate)?.toISOString(),
							brokerGroup,
							canViewClient,
						}
					: null;

				return { data };
			},
		}),
		// Defining an endpoint to get properties of a specific client
		getClientProperties: builder.query({
			providesTags: (result, error, clientId) => [{ type: 'CLIENT', id: clientId }],
			queryFn: async (clientId, { dispatch, getState }, _extraOptions, baseQuery) => {
				try {
					const useTestJson = clientId === testClientJson?.clientRow?.clientId;

					const response = useTestJson
						? [
								testClientJson?.clientRow,
								testClientJson?.insight,
								testClientJson?.broker,
							]
						: await Promise.all([
								dispatch(
									clientsApi.endpoints.getClientRow.initiate(clientId),
								).unwrap(),
								dispatch(
									clientsApi.endpoints.getClientInsight.initiate(clientId),
								).unwrap(),
							]);

					console.log('✅ ~ getClientProperties queryFn:', response);

					let [clientRow, insight, broker] = response;

					if (!clientRow) {
						return { data: { clientRow: null, insight: null, broker: null } };
					}

					const userData = await getActingUser(dispatch, getState);

					// A client's owner outside the client's broker group can't read the broker
					// group (Firestore rules); show the client without it.
					broker = await dispatch(
						userApi.endpoints.getBrokerGroupDetails.initiate({
							docId: useTestJson ? userData?.brokerGroupId : clientRow?.brokerGroupId,
						}),
					)
						.unwrap()
						.catch((error) => {
							console.warn('Could not read the client broker group', error);
							return null;
						});

					if (useTestJson) {
						const resolvedClientId = clientRow?.clientId;
						clientRow = {
							...clientRow,
							id: resolvedClientId,
							clientId: resolvedClientId,
							docId: resolvedClientId,
							brokerGroupId: userData?.brokerGroupId,
							ownerId: userData?.hubspotId,
						};
					}

					return { data: { clientRow, insight, broker } };
				} catch (error) {
					console.log('👾 ~ transform getClientProperties error response', error);
					return {
						error,
					};
				}
			},
		}),
		getClientPolicies: builder.query({
			providesTags: (result, error, { clientId }) => [
				{ type: 'CLIENT_POLICIES', id: clientId },
			],
			keepUnusedDataFor: 300, // Cache will only clear after 300 seconds (5 minutes)
			query: ({ insightId, transactionType, clientId, dealYearFolder, status, typeId }) => ({
				url: process.env.REACT_APP_RETRIEVE_CLIENT_INSIGHT_POLICYS,
				method: 'POST',
				body: { insightId, transactionType, clientId, dealYearFolder, status, typeId },
			}),
			transformResponse: (response) => {
				console.log('👾 ~ here are all the clients policys', response);
				return response.data;
			},
		}),
		updateClientRow: builder.mutation({
			// This tag indicates that this mutation invalidates the list of clients
			invalidatesTags: (result, error, { clientId }) => [
				{ type: 'CLIENTS', id: 'LIST' },
				{ type: 'CLIENT', id: clientId },
				{ type: 'CLIENT_POLICIES', id: clientId },
			],
			queryFn: async ({ clientId, docId, properties }, { getState, dispatch }) => {
				// Create a copy of properties to modify
				const updatedProperties = { ...properties };

				const userData = await getActingUser(dispatch, getState);

				const patchResult = dispatch(
					clientsApi.util.updateQueryData('getClientProperties', clientId, (draft) => {
						if (draft?.clientRow) {
							draft.clientRow = {
								...draft.clientRow,
								...updatedProperties,
							};
						}
					}),
				);

				const patchClientRowResult = dispatch(
					clientsApi.util.updateQueryData('getClientRow', clientId, (draft) => {
						if (draft) {
							// For objects, you need to merge properly
							Object.assign(draft, updatedProperties);
						}
					}),
				);

				try {
					const collectionRef = collection(firestore, 'clients');

					const ref = docId
						? doc(collectionRef, docId)
						: (await getClientById(clientId, userData)).ref;

					const sanitisedProperties = Object.fromEntries(
						Object.entries(updatedProperties).filter(([_, v]) => v !== undefined),
					);

					const result = await updateDoc(ref, sanitisedProperties);

					if ('referralPartner' in sanitisedProperties) {
						invalidateReferralPartnerClientCounts(dispatch);
						setTimeout(() => {
							dispatch(triggerRefresh());
						}, 10000);
					}

					return { data: result };
				} catch (error) {
					patchResult.undo();
					patchClientRowResult.undo();
					return { error: { status: 'CUSTOM_ERROR', error: error.message } };
				}
			},
		}),
		checkClientExistsInInsight: builder.query({
			query: ({ userId, companyName, email }) => ({
				url: process.env.REACT_APP_CHECK_CLIENT_EXISTS_INSIGHT,
				method: 'POST',
				body: { userId, companyName, email },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform checkClientExistsInInsight error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform checkClientExistsInInsight response', response);
				return response;
			},
		}),
		//
		// new CLIENT API endpoints
		// REACT_APP_CLIENT_API
		//
		listClients: builder.query({
			providesTags: (result, error, arg) => [{ type: 'CLIENTS', id: arg.filters }],
			query: ({ filters, includeFields }) => ({
				url: `${process.env.REACT_APP_CLIENT_API}/list`,
				method: 'POST',
				body: { includeFields, filters },
			}),
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform listClients error response', response);
				return response;
			},
			transformResponse: (response) => {
				console.log('👾 ~ transform listClients response', response);
				return response;
			},
		}),
		getClientsByReferralPartner: builder.query({
			providesTags: (result, error, referralPartnerEmail) => [
				{ type: 'CLIENTS_BY_REFERRAL_PARTNER', id: 'LIST' },
				...(referralPartnerEmail
					? [{ type: 'CLIENTS_BY_REFERRAL_PARTNER', id: referralPartnerEmail }]
					: []),
			],
			queryFn: async (referralPartnerEmail, { dispatch, getState }) => {
				if (!referralPartnerEmail) {
					return { data: [] };
				}

				try {
					const userData = await getActingUser(dispatch, getState);

					const docs = await queryViewableRecords({
						collectionRef: collection(firestore, CLIENT_COLLECTION),
						constraints: [where('referralPartner', '==', referralPartnerEmail)],
						user: userData,
					});

					return {
						data: docs.map((clientDoc) => ({
							id: clientDoc.id,
							clientId: clientDoc.id,
							hubspotId: clientDoc.data()?.hubspotId,
						})),
					};
				} catch (error) {
					console.error('Failed to get clients by referral partner:', error);
					return { error: { status: 500, message: error.message } };
				}
			},
		}),
		getReferralPartnerClientCounts: builder.query({
			providesTags: [{ type: 'CLIENTS_BY_REFERRAL_PARTNER', id: 'LIST' }],
			serializeQueryArgs: ({ queryArgs }) =>
				[...(queryArgs ?? [])]
					.map((email) => String(email).toLowerCase())
					.filter(Boolean)
					.sort()
					.join('|'),
			queryFn: async (emails, { dispatch, getState }) => {
				const uniqueEmails = [
					...new Set(
						(emails ?? [])
							.map((email) =>
								String(email ?? '')
									.trim()
									.toLowerCase(),
							)
							.filter(Boolean),
					),
				];
				if (uniqueEmails.length === 0) {
					return { data: {} };
				}

				try {
					const userData = await getActingUser(dispatch, getState);

					const entries = await Promise.all(
						uniqueEmails.map(async (referralPartnerEmail) => {
							const docs = await queryViewableRecords({
								collectionRef: collection(firestore, CLIENT_COLLECTION),
								constraints: [where('referralPartner', '==', referralPartnerEmail)],
								user: userData,
							});
							return [referralPartnerEmail, docs.length];
						}),
					);

					return { data: Object.fromEntries(entries) };
				} catch (error) {
					console.error('Failed to get referral partner client counts:', error);
					return { error: { status: 500, message: error.message } };
				}
			},
		}),
		createNewClient: builder.mutation({
			invalidatesTags: (result, error, arg) => {
				const clientId = result?.companyId ?? result?.data?.companyId ?? arg?.companyId;
				return [
					{ type: 'CLIENTS', id: 'LIST' },
					...(clientId
						? [
								{ type: 'CLIENT_ROW', id: clientId },
								{ type: 'CLIENT', id: clientId },
							]
						: []),
				];
			},
			query: ({ ...params }) => ({
				url: `${process.env.REACT_APP_CLIENT_API}/create`, // we would move this url to the base query eventually and just use /create here
				method: 'POST',
				body: {
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform /create response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform /create response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
				try {
					const { data } = await queryFulfilled;
					const clientId = data?.companyId;
					const assigneeUserId = arg?.firebase?.salesTeamUserId;
					if (clientId && assigneeUserId) {
						await createClientAssignedNotification({
							dispatch,
							getState,
							clientId,
							assigneeDocId: assigneeUserId,
						});
					}
				} catch {
					//
				}
			},
		}),
		updateClientx: builder.mutation({
			// CLIENT/CLIENT_ROW/CLIENT_INSIGHT are deliberately not invalidated here — the
			// response echoes back the authoritative clientRow/insightClient, which
			// onQueryStarted below patches directly, so those caches never need a refetch.
			// If getClient (a currently-unused query also tagged CLIENT) gains real callers,
			// either patch it here too or add CLIENT back to this list.
			invalidatesTags: (result, error, arg) => [
				{ type: 'CLIENTS', id: 'LIST' },
				{ type: 'CLIENT_POLICIES', id: arg.clientId },
			],
			query: ({ clientId, ...params }) => ({
				url: `${process.env.REACT_APP_CLIENT_API}/update`,
				method: 'POST',
				body: {
					clientId,
					...params,
				},
			}),
			transformResponse: (response) => {
				console.log('👾 ~ transform /update response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('👾 ~ transform /update response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, getState, queryFulfilled }) => {
				const clientId = arg?.clientId;
				const newOwnerId = arg?.firebase?.ownerDocId;
				const clientData = await dispatch(
					clientsApi.endpoints.getClientRow.initiate(clientId),
				).unwrap();
				const previousOwnerId = clientData.ownerDocId;

				// Optimistically reflect the change while the Insight round trip is in flight.
				// Fields resolved server-side from firebase.ownerDocId (ownerId, brokerGroupId,
				// salesTeam) aren't predictable here — they only appear once the final patch
				// below applies the server's response, not in this optimistic guess.
				const optimisticClientRowFields = {
					...deriveClientRowFieldsFromInsight(arg?.insight),
					...arg?.firebase,
				};

				const hasFirebaseFields = Object.keys(optimisticClientRowFields).length > 0;
				const hasInsightFields = arg?.insight && Object.keys(arg.insight).length > 0;

				const patches = [];
				if (clientId && (hasFirebaseFields || hasInsightFields)) {
					patches.push(
						dispatch(
							clientsApi.util.updateQueryData(
								'getClientProperties',
								clientId,
								(draft) => {
									if (hasFirebaseFields && draft?.clientRow) {
										Object.assign(draft.clientRow, optimisticClientRowFields);
									}
									if (hasInsightFields && draft?.insight) {
										Object.assign(draft.insight, arg.insight);
									}
								},
							),
						),
					);
				}
				if (clientId && hasFirebaseFields) {
					patches.push(
						dispatch(
							clientsApi.util.updateQueryData('getClientRow', clientId, (draft) => {
								if (draft) Object.assign(draft, optimisticClientRowFields);
							}),
						),
					);
				}
				if (clientId && hasInsightFields) {
					patches.push(
						dispatch(
							clientsApi.util.updateQueryData(
								'getClientInsight',
								clientId,
								(draft) => {
									if (draft) Object.assign(draft, arg.insight);
								},
							),
						),
					);
				}

				try {
					const { data } = await queryFulfilled;

					// Replace the optimistic guess with the authoritative values the server
					// actually wrote, instead of invalidating and waiting on a refetch.
					if (clientId && (data?.clientRow || data?.insightClient)) {
						dispatch(
							clientsApi.util.updateQueryData(
								'getClientProperties',
								clientId,
								(draft) => {
									if (data?.clientRow && draft?.clientRow) {
										Object.assign(draft.clientRow, data.clientRow);
									}
									if (data?.insightClient && draft?.insight) {
										Object.assign(draft.insight, data.insightClient);
									}
								},
							),
						);
					}
					if (clientId && data?.clientRow) {
						dispatch(
							clientsApi.util.updateQueryData('getClientRow', clientId, (draft) => {
								if (draft) Object.assign(draft, data.clientRow);
							}),
						);
					}
					if (clientId && data?.insightClient) {
						dispatch(
							clientsApi.util.updateQueryData(
								'getClientInsight',
								clientId,
								(draft) => {
									if (draft) Object.assign(draft, data.insightClient);
								},
							),
						);
					}

					if (
						clientId &&
						newOwnerId != null &&
						String(previousOwnerId ?? '') !== String(newOwnerId)
					) {
						await createClientAssignedNotification({
							dispatch,
							getState,
							clientId,
							assigneeDocId: newOwnerId,
						});
					}
				} catch {
					patches.forEach((patch) => patch.undo());
				}
			},
		}),
		batchGetClients: builder.query({
			queryFn: async (clientIds, { dispatch, getState }) => {
				if (!clientIds.length) return { data: [] };

				const dataByClientId = getClientsFromCache(getState(), clientIds);

				const missingFromCache = clientIds.filter((id) => !dataByClientId.has(id));
				if (missingFromCache.length) {
					const fromTypesense = await getClientsFromTypesense(missingFromCache, dispatch);
					for (const [id, data] of fromTypesense) dataByClientId.set(id, data);
				}

				const missingFromTypesense = clientIds.filter((id) => !dataByClientId.has(id));
				if (missingFromTypesense.length) {
					const fromRtk = await getClientsFromRtk(missingFromTypesense, dispatch);
					for (const [id, data] of fromRtk) dataByClientId.set(id, data);
				}

				return { data: Array.from(dataByClientId.values()) };
			},
		}),
		listInformedConsents: builder.query({
			providesTags: (result, error, clientId) => [{ type: 'INFORMED_CONSENT', id: clientId }],
			queryFn: async (clientId) => {
				if (!clientId) {
					return { data: [] };
				}

				try {
					const snapshot = await getDocs(
						query(
							collection(firestore, INFORMED_CONSENTS_COLLECTION),
							where('clientId', '==', String(clientId)),
						),
					);

					const records = snapshot.docs
						.map(mapInformedConsentDoc)
						.sort((a, b) => getConsentRecordTime(b) - getConsentRecordTime(a));

					return { data: records };
				} catch (error) {
					console.error('[listInformedConsents]', error);
					return {
						error: {
							status: error?.code ?? 500,
							data: error?.message ?? 'Failed to load informed consent history',
						},
					};
				}
			},
		}),
		recordInformedConsent: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'INFORMED_CONSENT', id: arg.clientId },
			],
			queryFn: async (
				{
					clientId,
					dealId,
					brokerGroupId,
					dateReceived,
					notes,
					attachments = [],
					notApplicable = false,
				},
				{ dispatch, getState },
			) => {
				if (!clientId) {
					return {
						error: {
							status: 400,
							data: 'Client is required',
						},
					};
				}

				const receivedDate = safelyParseDate(dateReceived);
				if (!notApplicable && !receivedDate) {
					return {
						error: {
							status: 400,
							data: 'Date received is required',
						},
					};
				}

				try {
					const userData = await getActingUser(dispatch, getState);
					const email = getState()?.msalAccount?.account?.username;

					const collectionRef = collection(firestore, INFORMED_CONSENTS_COLLECTION);
					const docRef = doc(collectionRef);
					const now = Timestamp.now();
					const recordedById = userData?.userId;
					const payload = {
						informedConsentId: docRef.id,
						clientId,
						dealId: dealId ?? null,
						brokerGroupId: brokerGroupId ?? userData?.brokerGroupId ?? null,
						dateReceived: receivedDate ? Timestamp.fromDate(receivedDate) : null,
						notes: notes?.trim() || '',
						attachments,
						notApplicable: Boolean(notApplicable),
						recordedById: recordedById ?? null,
						recordedByName: userData?.name ?? email ?? null,
						createdAt: now,
						updatedAt: now,
					};

					await setDoc(docRef, payload);

					return {
						data: {
							id: docRef.id,
							...payload,
							dateReceived: receivedDate ? receivedDate.toISOString() : null,
							createdAt: now.toDate().toISOString(),
							updatedAt: now.toDate().toISOString(),
						},
					};
				} catch (error) {
					console.error('[recordInformedConsent]', error);
					return {
						error: {
							status: error?.code ?? 500,
							data: error?.message ?? 'Failed to record informed consent',
						},
					};
				}
			},
		}),
		deleteInformedConsent: builder.mutation({
			invalidatesTags: (result, error, arg) => [
				{ type: 'INFORMED_CONSENT', id: arg.clientId },
			],
			queryFn: async ({ informedConsentId }) => {
				if (!informedConsentId) {
					return {
						error: {
							status: 400,
							data: 'Informed consent is required',
						},
					};
				}

				try {
					await deleteDoc(
						doc(firestore, INFORMED_CONSENTS_COLLECTION, informedConsentId),
					);
					return { data: { id: informedConsentId } };
				} catch (error) {
					console.error('[deleteInformedConsent]', error);
					return {
						error: {
							status: error?.code ?? 500,
							data: error?.message ?? 'Failed to delete informed consent',
						},
					};
				}
			},
		}),
	}),
});

// Exporting auto-generated hooks for the defied endpoints
export const {
	useGetClientInsightQuery,
	useLazyGetClientInsightQuery,
	useGetClientPropertiesQuery,
	useLazyGetClientPropertiesQuery,
	useUpdateClientRowMutation,
	useGetClientPoliciesQuery,
	useGetClientRowQuery,
	useLazyGetClientRowQuery,
	useCheckClientExistsInInsightQuery,
	useLazyCheckClientExistsInInsightQuery,
	useCreateNewClientMutation,
	useUpdateClientxMutation,
	useListClientsQuery,
	useLazyListClientsQuery,
	useBatchGetClientsQuery,
	useLazyBatchGetClientsQuery,
	useGetClientsByReferralPartnerQuery,
	useLazyGetClientsByReferralPartnerQuery,
	useGetReferralPartnerClientCountsQuery,
	useListInformedConsentsQuery,
	useRecordInformedConsentMutation,
	useDeleteInformedConsentMutation,
} = clientsApi;
